import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

export function IconCloud3D({
  images = [],
  radius = 120,
  rotationSpeed = 0.006,
  pauseOnHover = true,
  maxTiltX = 0.35,
  maxTiltY = 0.6,
  followDamping = 0.08,
  maxIcons = 40,
  iconSize = 22,
  radiusJitter = 5,
  className = ''
}) {
  const mountRef = useRef(null);
  const animRef = useRef({ hovering: false, targetX: 0, targetY: 0 });
  // Store Three.js objects for cleanup to prevent memory leaks
  const resourcesRef = useRef({ scene: null, renderer: null, sprites: [] });

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const isMobile = window.innerWidth < 768;
    const width = mount.clientWidth || 300;
    const height = mount.clientHeight || 300;
    
    // Optimization: Lower DPR on mobile to prevent GPU overheating
    const dpr = Math.min(window.devicePixelRatio, isMobile ? 1.5 : 2);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.z = radius * 3.5;

    const renderer = new THREE.WebGLRenderer({ 
      antialias: !isMobile, // Disable antialias on mobile for performance
      alpha: true,
      powerPreference: "high-performance" 
    });
    
    renderer.setPixelRatio(dpr);
    renderer.setSize(width, height);
    mount.appendChild(renderer.domElement);
    
    resourcesRef.current.renderer = renderer;
    resourcesRef.current.scene = scene;

    const group = new THREE.Group();
    scene.add(group);
    scene.add(new THREE.AmbientLight(0xffffff, 1.2));

    const loader = new THREE.TextureLoader();
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));
    const base = Array.isArray(images) ? images.filter(Boolean) : [];
    
    // Cap icons for mobile performance
    const actualMaxIcons = isMobile ? Math.min(maxIcons, 25) : maxIcons;
    const sources = [];
    if (base.length > 0) {
      for (let i = 0; i < actualMaxIcons; i++) {
        sources.push(base[i % base.length]);
      }
    }

    sources.forEach((src, i) => {
      loader.load(src, (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.minFilter = THREE.LinearFilter; // Faster than MipMapping
        
        const material = new THREE.SpriteMaterial({ 
          map: texture, 
          opacity: 0.9,
          transparent: true 
        });
        
        const sprite = new THREE.Sprite(material);
        const t = i / Math.max(sources.length - 1, 1);
        const y = 1 - 2 * t;
        const r = Math.sqrt(1 - y * y);
        const phi = i * goldenAngle;

        const jitter = (Math.random() * 2 - 1) * radiusJitter;
        sprite.position.set(
          Math.cos(phi) * r * (radius + jitter),
          y * (radius + jitter),
          Math.sin(phi) * r * (radius + jitter)
        );

        sprite.scale.set(iconSize, iconSize, 1);
        group.add(sprite);
        
        // Track for cleanup
        resourcesRef.current.sprites.push({ material, texture });
      });
    });

    let rafId;
    const animate = () => {
      if (animRef.current.hovering && pauseOnHover) {
        group.rotation.x += (animRef.current.targetX - group.rotation.x) * followDamping;
        group.rotation.y += (animRef.current.targetY - group.rotation.y) * followDamping;
      } else {
        group.rotation.x += rotationSpeed;
        group.rotation.y += rotationSpeed * 0.2; 
      }
      
      renderer.render(scene, camera);
      rafId = requestAnimationFrame(animate);
    };
    animate();

    // Handlers
    const onResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };

    const onMove = (e) => {
      const rect = mount.getBoundingClientRect();
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      const y = e.touches ? e.touches[0].clientY : e.clientY;
      const nx = ((x - rect.left) / rect.width) * 2 - 1;
      const ny = ((y - rect.top) / rect.height) * 2 - 1;
      animRef.current.targetY = nx * maxTiltY;
      animRef.current.targetX = -ny * maxTiltX;
    };

    window.addEventListener('resize', onResize);
    mount.addEventListener('mousemove', onMove);
    mount.addEventListener('touchstart', onMove, { passive: true });
    mount.addEventListener('mouseenter', () => { animRef.current.hovering = true; });
    mount.addEventListener('mouseleave', () => { animRef.current.hovering = false; });

    return () => {
      window.removeEventListener('resize', onResize);
      mount.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(rafId);
      
      // CRITICAL: Disposal of WebGL Resources
      resourcesRef.current.sprites.forEach(({ material, texture }) => {
        material.map.dispose();
        material.dispose();
        texture.dispose();
      });
      
      renderer.dispose();
      renderer.forceContextLoss(); // Releases the GPU context immediately
      
      if (mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, [images, radius, rotationSpeed, maxIcons, iconSize, radiusJitter, pauseOnHover]);

  return <div ref={mountRef} className={className} style={{ width: '100%', height: '100%' }} />;
}